import { isNil } from 'lodash-es';
import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { AmoFormQuestionType } from '../constants/lookups';
import { MiddlewareInjectToken } from '../constants/tokens';
import { Apis, MiddlewareConfig } from '../types';
import { AccountApplicationResponse, FormattedAccountApplication } from '../types/Account.type';
import {
    AnswerFormFieldPayload,
    AnswerMultipleFormFieldsPayload,
    Form,
    FormAnswer,
    FormApplicant,
    FormApplication,
    FormCountry,
    FormDocument,
    FormDocumentType,
    FormMultiAnswer,
    FormQuestionsList,
    FormQuestionType,
    FormRuleSet,
    FormState,
    GetFormApplicationWithAnswersPayload,
    GetFormPayload,
    SubmitFormPayload,
} from '../types/Amo.type';
import { generatePassword } from '../utils';

@injectable()
export class AmoApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
    ) {
        super(apis, config);
    }

    async getAccountApplication({
        accountNumber,
        preIpo = false,
        branchId,
        formId,
        firmId,
        initiatorId,
    }: {
        accountNumber: string;
        preIpo: boolean;
        branchId: number;
        userId?: number;
        formId: number;
        firmId: number;
        initiatorId: number;
    }): Promise<FormattedAccountApplication | null> {
        let data: AccountApplicationResponse;

        if (preIpo) {
            const response = await this.apis.uma!.getApiClient().post<any>('../../amo/api/applications/list', {
                filters: {
                    answers: [
                        {
                            question_system_identifier: 'application.import.account-number',
                            answer: accountNumber,
                        },
                    ],
                },
                with: ['user'],
            });
            data = response.data;
        } else {
            const response = await this.apis.uma!.getApiClient().post<any>('../../amo/api/applications/list', {
                search: accountNumber,
                with: ['user'],
            });

            data = response.data;
        }

        const application = data.applications[0];

        if (application) {
            return {
                id: application.id,
                uid: application.uid,
                user: {
                    id: application.user.uma_id,
                    email: application.user.email,
                    firstName: application.user.first_name,
                    lastName: application.user.last_name,
                },
                branchId,
                firmId,
                initiatorId,
                formId: formId || application.form_id,
                isSubmitted: application.submitted_at !== null,
                w8ExpiresAt: application.w8_expires_at,
                w8WarningsAt: application.w8_warning_at,
                w8LastCertified: application.w8_last_certified,
            };
        } else {
            return null;
        }
    }

    async createAccountApplication({
        accountNumber,
        firstName,
        lastName,
        email,
        branchId,
        formId,
        firmId,
        initiatorId,
        preIpo,
    }: {
        accountNumber: string;
        firstName: string;
        lastName: string;
        email: string;
        branchId: number;
        formId: number;
        firmId: number;
        initiatorId: number;
        preIpo: boolean;
    }): Promise<FormattedAccountApplication> {
        let userId = 0;

        const applicationResponse = await this.apis
            .uma!.getApiClient()
            .post<any>('../../amo/api/uma/v2/users/b2c/list', {
                search: email,
                pagination: {
                    page: 1,
                    size: 10,
                },
                filters: { firm_ids: [firmId], branch_ids: [branchId] },
            });

        const usersData = applicationResponse.data;

        if (usersData.users_list?.length > 0) {
            userId = usersData.users_list[0].id;
        } else {
            const generatedPassword = generatePassword({
                numberOfCapitalLetters: 4,
                numbers: 3,
                numberOfLowerCaseLetters: 5,
            });

            const response = await this.apis.uma!.getApiClient().post<any>('../../amo/api/uma/v1/users/b2c', {
                first_name: firstName,
                last_name: lastName,
                email: email,
                password: generatedPassword,
                should_reset_password: true,
                phone: '',
                allow_feedback: true,
                permissions: [],
                branches: [branchId],
                roles: [1090],
                firm_id: firmId,
            });

            userId = response.data.b2c_user.id;
        }

        const application = await this.getAccountApplication({
            accountNumber,
            branchId,
            formId,
            firmId,
            initiatorId,
            preIpo,
        });

        if (application) {
            return application;
        }

        let accountNumberQuestionId = 0;
        let emailQuestionId = 0;

        const createApplicationResponse = await this.apis
            .uma!.getApiClient()
            .post<any>('../../amo/api/applications/create', {
                uma_user_id: userId,
                uma_branch_id: branchId,
                uma_initiator_id: initiatorId,
                form_id: formId,
                with: ['user'],
            });

        const newApplication = createApplicationResponse.data.application;

        const formsResponse = await this.apis.uma!.getApiClient().post<any>('../../amo/api/forms/get', {
            id: formId,
            with: ['pages.sections.questions'],
        });

        const formsData = formsResponse.data;

        formsData.form.pages.forEach((page: any) => {
            page.sections.forEach((section: any) => {
                section.questions.forEach((question: any) => {
                    if (question.system_identifier === 'application.import.account-number') {
                        accountNumberQuestionId = question.id;
                    }

                    if (question.system_identifier === 'applicant.primary.email.personal') {
                        emailQuestionId = question.id;
                    }
                });
            });
        });

        if (accountNumberQuestionId) {
            await this.apis.uma!.getApiClient().post('../../amo/api/applications/answer', {
                application_id: newApplication.id,
                question_id: accountNumberQuestionId,
                data: accountNumber,
            });
        }

        if (emailQuestionId) {
            await this.apis.uma!.getApiClient().post('../../amo/api/applications/answer', {
                application_id: newApplication.id,
                question_id: emailQuestionId,
                data: newApplication.user.email,
            });
        }

        return {
            id: newApplication.id,
            uid: newApplication.uid,
            user: {
                id: newApplication.user.uma_id,
                email: newApplication.user.email,
                firstName: newApplication.user.first_name,
                lastName: newApplication.user.last_name,
            },
            branchId,
            firmId: newApplication.uma_firm_id,
            initiatorId,
            formId,
            isSubmitted: newApplication.submitted_at !== null,
        };
    }

    // Get form by ID
    async getForm({ formId }: GetFormPayload): Promise<Form> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/forms/get', {
            id: formId,
            with: [
                'pages.translations',
                'pages.sections.translations',
                'pages.sections.questions.translations',
                'pages.sections.questions.parent_questions.parent_question',
                'pages.sections.questions.parent_questions.parent_option',
                'pages.sections.questions.options.parent_options.parent_option',
                'pages.sections.questions.options.translations',
                'pages.sections.questions.description.translations',
            ],
        });
        return res.data;
    }

    // Get countries list
    async getCountries(): Promise<FormCountry[]> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/countries/list');
        return res.data.countries;
    }

    // Get states list
    async getStates(): Promise<FormState[]> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/states/list');
        return res.data.states;
    }

    // Get rule sets
    async getRuleSets(): Promise<FormRuleSet[]> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/rule-sets/list');
        return res.data.rule_sets;
    }

    // Get question types
    async getQuestionTypes(): Promise<FormQuestionType[]> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/question-types/list');
        return res.data.question_types;
    }

    // Get document types
    async getDocumentTypes(): Promise<FormDocumentType[]> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/document-types/list');
        return res.data.document_types;
    }

    // Get application with answers
    async getApplicationWithAnswers({ applicationId }: GetFormApplicationWithAnswersPayload): Promise<FormApplication> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/get', {
            application_id: applicationId,
            with: [
                'answers.option.translations',
                'answers.question.translations',
                'answers.document.current_status.timestamps',
                'current_status',
                'form',
                'statuses.timestamps',
                'documents.current_status.timestamps',
                'documents.answer.question.translations',
                'branch.services.settings',
                'timestamps',
                'current_custodian_request',
                'applicants.latest_background_check',
                'applicants.details',
                'current_background_check_status',
                'current_custodian_status',
            ],
        });
        return res.data.application;
    }

    // Delete answer from form
    async deleteAnswerFromForm({
        application_id,
        question_id,
    }: {
        application_id: number;
        question_id: number;
    }): Promise<void> {
        await this.apis.uma!.getApiClient().post('../../amo/api/applications/delete-answer', {
            application_id,
            question_id,
        });
    }

    // Get form document
    async getFormDocument({
        application_id,
        document_id,
    }: {
        application_id: number;
        document_id: number;
    }): Promise<FormDocument> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/documents/get', {
            application_id,
            document_id,
        });
        return res.data.document;
    }

    // Answer form field
    async answerFormField({ application_id, data, question_id }: AnswerFormFieldPayload): Promise<FormAnswer> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/answer', {
            application_id,
            data,
            question_id,
            with: ['option.translations', 'question.translations', 'document.current_status.timestamps'],
        });
        return res.data.answer;
    }

    // Answer form field
    async answerFormFieldFile(formData: FormData): Promise<FormAnswer> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/answer', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return res.data.answer;
    }

    // Answer multiple form fields
    async answerMultipleFormFields({
        application_id,
        answers,
    }: AnswerMultipleFormFieldsPayload): Promise<FormMultiAnswer> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/answer-multiple', {
            application_id,
            answers,
        });
        return res.data.answers;
    }

    // Submit form
    async submitForm({ application_id }: SubmitFormPayload): Promise<FormApplication> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/submit', {
            application_id,
        });
        return res.data.application;
    }

    // Run background check
    async runBackgroundCheck({ application_id }: SubmitFormPayload): Promise<FormApplication> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/background-check', {
            application_id,
            with: ['applicants.latest_background_check'],
        });
        return res.data.application;
    }

    // Get background check status
    async getBackgroundCheckStatus({ application_id }: SubmitFormPayload): Promise<{
        application: FormApplication;
        applicants: {
            type: string;
            isCompleted: boolean;
            status: string;
            docVerificationRequired: boolean;
            docVerificationPassed: boolean;
        }[];
    }> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/get', {
            application_id,
            with: ['applicants.latest_background_check'],
        });
        return {
            application: res.data.application,
            applicants: res.data.application.applicants.map((applicant: FormApplicant) => {
                return {
                    type: applicant.type,
                    isCompleted: applicant.latest_background_check?.global_id_status === 'COMPLETE' || false,
                    status: applicant.latest_background_check?.status || 'pending',
                    docVerificationRequired:
                        applicant.latest_background_check?.response?.check_status === 'DOCUMENTATION_REQUIRED' || false,
                    docVerificationPassed: this.checkIfDocumentVerificationPassed(applicant.type, res.data.application),
                };
            }),
        };
    }

    // Verify documents for an applicant
    async verifyDocuments({ application_id, applicant_type }: SubmitFormPayload): Promise<FormApplication> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/verify-documents', {
            application_id,
            applicant_type,
        });
        return res.data.application;
    }

    // Submit form to custodian
    async submitFormToCustodian({ application_id }: SubmitFormPayload): Promise<FormApplication> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/custodian/send', {
            application_id,
            with: ['current_custodian_request'],
        });
        return res.data.application;
    }

    // Get custodian request status
    async getCustodianRequestStatus({ application_id }: SubmitFormPayload): Promise<FormApplication> {
        const res: any = await this.apis.uma!.getApiClient().post('../../amo/api/applications/get', {
            application_id,
            with: ['current_custodian_request'],
        });
        return res.data.application;
    }

    private checkIfDocumentVerificationPassed(applicantType: string, application: FormApplication): boolean {
        return application.applicants.every(
            (applicant) =>
                applicant.type === applicantType &&
                applicant.latest_background_check &&
                applicant.latest_background_check.status === 'success',
        );
    }

    async submitFormWithChecks({ application_id }: SubmitFormPayload): Promise<any> {
        // This method can be used to
        // perform additional checks before submitting the form
        const application = await this.getApplicationWithAnswers({ applicationId: application_id, decrypt: true });
        // Perform any necessary checks on the application data
        // For example, check if all required fields are filled

        application.answers.every((answer) => {
            return !isNil(answer.data);
        });
    }

    async recertifyW8(applicationId: string) {
        const response = await this.apis.uma
            ?.getApiClient()
            .post<any>('../../amo/api/applications/w8/recertify-without-changes', {
                application_id: applicationId,
            });
        return response?.data;
    }

    async getAgreements({ formId }: { formId: number }): Promise<FormQuestionsList> {
        const response = await this.apis.uma!.getApiClient().post<FormQuestionsList>('../../amo/api/questions/list', {
            filters: {
                form: formId.toString(),
                question_type: AmoFormQuestionType.Agreement,
                is_hidden: false,
            },
            paginate: {
                per_page: 100,
                page: 1,
            },
            with: ['translations', 'options.translations', 'description.translations'],
        });

        return response.data;
    }
}
